/**
 * Regras puras da fila de entregas (Planejamento de Entregas): como a linha
 * da planilha vira a `linha` exibida na tabela e no calendario, como a
 * linha vira evento do <Calendar> e como os filtros da tela se aplicam.
 * Sem React — testavel em isolamento.
 */
import { diasUteis } from "../../utils/calculos";
import { toDisplayText } from "../../utils/data";
import { parseMarceneiro } from "../../utils/entregas";
import { F } from "../../utils/gerencialFields";
import { buildDeadlineStatus, toDate } from "../../utils/status";

export const EMPTY_VALUE = "—";
export const SEM_MARCENEIRO = "Sem marceneiro";
/* Rotulo que diz a regua do evento tracejado: nao e uma entrega programada,
   e a data prevista em contrato de um ambiente que ainda nem tem lote. */
export const PREVISAO_ROTULO = "Previsão · sem lote";

function montarLinha(row, index, { dataLimite, previsao }) {
  const dataReprogramada = row[F.DATA_REPROGRAMADA];
  const status = buildDeadlineStatus(dataLimite);
  const chegadaMdf = toDate(row[F.MDF_BORDO]);
  const diasNaFilaMaquinas = chegadaMdf ? diasUteis(chegadaMdf, new Date()) : null;

  return {
    id: `${row[F.NUMERO_DO_PEDIDO] || row[F.NOME_DO_CLIENTE] || "entrega"}-${row[F.AMBIENTE] || index}-${index}`,
    cliente: toDisplayText(row[F.NOME_DO_CLIENTE], EMPTY_VALUE),
    ambiente: toDisplayText(row[F.AMBIENTE], EMPTY_VALUE),
    numeroLote: toDisplayText(row[F.NUMERO_DO_LOTE], EMPTY_VALUE),
    marceneiro: toDisplayText(row[F.MARCENEIRO_RESPONSAVEL], SEM_MARCENEIRO),
    dataReprogramada,
    dataLimite,
    marcenariaTermino: row[F.MARCENARIA_TERMINO],
    chegadaMdf,
    diasNaFilaMaquinas,
    valor: row[F.VALOR_DO_AMBIENTE],
    observacoesGerais: row[F.OBSERVACOES_GERAIS],
    status: status.status,
    statusVariant: status.statusVariant,
    previsao,
    raw: row,
    searchText: [
      row[F.NOME_DO_CLIENTE],
      row[F.AMBIENTE],
      row[F.NUMERO_DO_LOTE],
      row[F.MARCENEIRO_RESPONSAVEL],
      status.status,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase(),
  };
}

/** Linha da fila: o prazo e a data reprogramada ou, na falta, a de contrato. */
export function buildLinha(row, index) {
  const dataLimite = row[F.DATA_REPROGRAMADA] || row[F.ENTREGA_PREVISTA];
  return montarLinha(row, index, { dataLimite, previsao: false });
}

/**
 * Linha de um ambiente ainda sem lote (isEntregaSemLote). O prazo e SEMPRE
 * a entrega prevista em contrato (col. N): antes do lote nao existe
 * reprogramacao que valha. O id ganha prefixo para nao colidir com a fila.
 */
export function buildLinhaPrevisao(row, index) {
  const linha = montarLinha(row, index, {
    dataLimite: row[F.ENTREGA_PREVISTA],
    previsao: true,
  });
  return { ...linha, id: `previsao-${linha.id}` };
}

/**
 * Evento do <Calendar> para uma linha da fila, na sua data limite
 * (reprogramada ou, na falta, a prevista em contrato). `null` quando a
 * linha nao tem data — a tela lista essas em "sem prazo definido".
 */
export function linhaParaEvento(linha) {
  const eventDate = toDate(linha.dataLimite);
  if (!eventDate) return null;
  const marc = parseMarceneiro(linha.marceneiro);
  return {
    id: linha.id,
    date: eventDate,
    groupLabel: linha.cliente,
    title: linha.ambiente,
    // Subtitulo do card (visivel na view Dia): "Lote X · Marceneiro".
    subtitle:
      [
        linha.numeroLote && linha.numeroLote !== EMPTY_VALUE
          ? `Lote ${linha.numeroLote}`
          : null,
        marc.semMarceneiro ? null : marc.nome,
      ]
        .filter(Boolean)
        .join(" · ") || null,
    tone: linha.statusVariant,
    payload: linha,
  };
}

/**
 * Evento tracejado do <Calendar> para uma previsao (ambiente sem lote): mesma
 * data/tom da linha, subtitulo fixo dizendo o que ele e (+ marceneiro, se ja
 * houver) e a flag `previsao` que o calendario desenha com contorno
 * tracejado. `null` quando a col. N esta vazia.
 */
export function previsaoParaEvento(linha) {
  const evento = linhaParaEvento(linha);
  if (!evento) return null;
  const marc = parseMarceneiro(linha.marceneiro);
  return {
    ...evento,
    subtitle: [PREVISAO_ROTULO, marc.semMarceneiro ? null : marc.nome]
      .filter(Boolean)
      .join(" · "),
    previsao: true,
  };
}

/**
 * Filtros da tela (busca livre ja normalizada em minusculas + selects de
 * marceneiro e status). "TODOS" ou ausencia desliga o filtro.
 */
export function passaNosFiltros(
  linha,
  { query = "", marceneiro = "TODOS", status = "TODOS" } = {}
) {
  if (query && !linha.searchText.includes(query)) return false;
  if (marceneiro !== "TODOS" && linha.marceneiro !== marceneiro) return false;
  if (status !== "TODOS" && linha.status !== status) return false;
  return true;
}
